/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ActionName, ClusterActionResponse, ResponseStatus } from './cluster-actions';
import { CacheName, PaginatedDataResponse } from './common';
import { NodeId } from './node';

export type QueryId = string;
export type QueryResponseResultId = string;

export interface QueryBase<T extends QueryType> {
  type: T;
  name: string;
  id: QueryId;
}

export enum QueryType {
  SCAN = 'ScanQuery',
  SQL = 'SqlQuery',
}

export type SqlQuery = SqlQueryBase & SqlQueryAdvancedSettings;

export type FrontendQueryString = string & { _tag_: 'FrontendQueryString' };
export type FrontendQueryParameters = Record<string, string>;

export interface SqlQueryBase extends QueryBase<QueryType.SQL> {
  query: FrontendQueryString;
  parameters: FrontendQueryParameters;
}

export type AdvancedSettingsSelectedNode = string | null;

export interface SqlQueryAdvancedSettings {
  defaultSchema: string | null;
  selectedNodeConsistentId: AdvancedSettingsSelectedNode;
  distributedJoins: boolean;
  enforceJoinOrder: boolean;
  lazy: boolean;
}

export interface ScanQuery extends QueryBase<QueryType.SCAN> {
  cacheName: string;
  nodeConsistentId: AdvancedSettingsSelectedNode;
  /**
   * Integer from 0 to 2147483647
   */
  partition: number | null;
  keyFilterRegex: string | null;
  valueFilterRegex: string | null;
}

export type Query = SqlQuery | ScanQuery;

export enum IgniteQueryType {
  SPI = 'SPI',
  SCAN = 'SCAN',
  SQL_FIELDS = 'SQL_FIELDS',
  TEXT = 'TEXT',
  SET = 'SET',
}

export enum QuerySessionStatus {
  RUNNING = 'RUNNING',
  FAILED = 'FAILED',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
  UNKNOWN = 'UNKNOWN',
}

export const QuerySessionStatuses = [null, ...Object.values(QuerySessionStatus)] as const;
export type QuerySessionType = (typeof QuerySessionStatuses)[number];

export interface ClusterActionSqlExecuteArgument {
  defaultSchema?: string;
  distributedJoins?: boolean;
  enforceJoinOrder?: boolean;
  pageSize: number;
  lazy?: boolean;
  collocated?: boolean;
  queryId: string;
  local?: boolean;
  queryText: IgniteQueryString;
  parameters: SqlExecuteQueryParameters;
}

export type IgniteQueryString = string & { _tag_: 'IgniteQueryString' };
export type SqlExecuteQueryParameters = string[];

export interface ClusterActionScanExecuteArgument {
  pageSize: number;
  queryId: string;
  cacheName: string;
  local: boolean;
  partition: number | null;
  keyFilterRegex: string | null;
  valueFilterRegex: string | null;
}

export interface ClusterActionSqlNextPageArgument {
  cursorId: string;
  pageSize: number;
  queryId: string;
}

export interface ClusterActionQueryResponseResult {
  columns: QueryField[];
  rows: (number | string | boolean)[][];
  hasMore: boolean;
  cursorId: string | null;
  duration: number;
  resultNodeId: NodeId;
  // additional FE fields that not exists in the response from  cluster action
  failed?: boolean;
  title?: string;
  id?: QueryResponseResultId;
  type?: QueryType.SQL | QueryType.SCAN;
}
export type GlobalQueryId = string;
export interface RunningQueryBase {
  nodeId: NodeId;
  globalQueryId: GlobalQueryId;
  schemaName: string;
  queryType: IgniteQueryType;
  query: string;
  cancellable: boolean;
  local: boolean;
  enforceJoinOrder: boolean;
  lazy: boolean;
  distributedJoins: boolean;
}

export interface ClusterActionRunningQueryResult extends RunningQueryBase {
  id: number;
  duration: number;
  startTime: number;
}

export interface ClusterActionQueryStatisticsResult {
  completions: number;
  executions: number;
  failures: number;
  lastStartTime: number;
  maxTime: number;
  minTime: number;
  totalTime: number;
  query: string;
  queryType: IgniteQueryType;
}

export interface ClusterActionCacheConfigurationResult {
  cacheName: CacheName;
  json: string;
}

export interface ClusterActionCacheArgument {
  cacheName: CacheName;
}

export interface ClusterActionCachesArgument {
  cacheNames: CacheName[];
}

export interface QueryField {
  fieldName: string;
  fieldTypeName: string;
  schemaName: string | null;
  typeName: string | null;
}

export interface SqlMetadata {
  cacheName: string;
  tableName: string;
  schemaName: string;
  fields: Record<string, string>;
}

export interface QueryExportMessage {
  errorMsg: string | null;
  id: QueryId;
  resourceId: string | null;
  status: ResponseStatus;
}

// TODO: CC-907 (ex: GG-28292)
export type ExecuteQueryResponseType<T extends ActionName> =
  | {
      job: ClusterActionResponse<T>;
      jobCount: number;
    }
  | ClusterActionResponse<T>;

export type QueryInitiatorId = string;

export type QuerySessionPaged = RunningQueryBase & {
  startedAt: number;
  status: QuerySessionStatus;
  duration: number | null;
};

export type QuerySessionResponse = PaginatedDataResponse<QuerySessionPaged>;

export type QueryAllSessionIdsResponse = {
  globalIds: GlobalQueryId[];
};

export type QuerySessionInfo = QuerySessionPaged & {
  queryInitiatorId: QueryInitiatorId;
} & (
    | {
        status: QuerySessionStatus.FAILED;
        failedReason: {
          stacktrace: string;
        };
      }
    | {
        status: Exclude<QuerySessionStatus, QuerySessionStatus.FAILED>;
        failedReason: null;
      }
  );

export type RunningQueryConfiguration = {
  duration: number;
};

export type RunningQueryConfigurationArgument = RunningQueryConfiguration;
export type RunningQueryConfigurationResponse = RunningQueryConfiguration;
